import { useEffect, useState } from 'react';
import type { CheckoutIo } from '../model/checkout_env.js';

type Photos = Readonly<Record<string, string | null>>;
type PhotosFor = NonNullable<CheckoutIo['getTravelerPhotos']>;

/**
 * The photo of each traveler of a summary that keeps only `{id,name}`, read
 * through the host's `io.getTravelerPhotos`. The URL is pre-signed and expires
 * (#2640), so it never rides in the block: it is asked for here, at render time.
 *
 * Fail-soft like the rest of the card: with no read, the read in flight or
 * failing, the id stays without a photo and the avatar falls back to initials.
 * Only the photos of the ids in question come out — an earlier list's answer
 * never leaks into the new one.
 */
export function useTravelerPhotos(io: CheckoutIo | null, ids: readonly string[]): Photos {
  const photosFor = io?.getTravelerPhotos;
  const key = ids.join(',');
  // The read enters the mark of what was loaded: with another read (another
  // identity on the host), the photos of the previous one do not serve while
  // the new answer is pending.
  const [loaded, setLoaded] = useState<{ key: string; source?: PhotosFor; photos: Photos }>({
    key: '',
    photos: {},
  });
  useEffect(() => {
    if (!photosFor || !key) return undefined;
    let cancelled = false;
    void photosFor(key.split(','))
      .then((photos) => {
        if (!cancelled) setLoaded({ key, source: photosFor, photos });
      })
      .catch(() => {
        // A photo is decoration: a failure leaves the initials, never an error.
        if (!cancelled) setLoaded({ key, source: photosFor, photos: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [key, photosFor]);
  return photosFor && loaded.source === photosFor && loaded.key === key ? loaded.photos : {};
}
